import React from 'react';
import { Link, RouteComponentProps, withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { FormattedMessage } from 'react-intl';
// Store
import { IRootState } from '../../../redux';
import { logoutUser } from '../../../redux/user/actions';
import { showSidebar, hideSidebar } from '../../../redux/sidebar/actions';
import { selectFolder, setSearchOutsideMailbox } from '../../../redux/mailbox/actions';
// Components
import { Button, Image, CustomLink, StaticResource } from '../..';
import { HeaderMenu } from '../HeaderMenuButton';
import MailSearch from '../../MailSearch/MailSearch';
// Styles
import styles from './userheader.module.scss';
// Assets
import images from '../../../assets/images/images';
// Config
import { ConfigContext } from '../../../providers';
import { staticConfig } from '../../../config/static';
// Helpers
import { isMediaSizeLessThanLG, isMediaSizeLessThanMD } from '../../../helpers/layout';
// Interfaces
import { ISelectedFolder } from '../../../interfaces/folder.interfaces';
import { HeaderTabs } from '../../Tabs';
import { getUserInfo } from '../../../services/login.service';

interface IUserHeaderState {
    showMobileSearch: boolean;
}

interface IUserHeaderProps {
    isEmpty?: boolean;
    isMailbox?: boolean;
    withLogo?: boolean;
    withSearch?: boolean;
}

class UserHeader extends React.Component<ReduxType, IUserHeaderState> {
    private buttonRef = React.createRef<HTMLAnchorElement>();
    private mailSearch = React.createRef<HTMLDivElement>();
    private sidebarButton = React.createRef<HTMLButtonElement>();

    constructor(props: ReduxType) {
        super(props);
        this.state = {
            showMobileSearch: false,
        };
        UserHeader.contextType = ConfigContext;
    }

    componentDidUpdate(prevProps: ReduxType): void {
        if (prevProps.mediaSize !== this.props.mediaSize) {
            this.setState({
                showMobileSearch: isMediaSizeLessThanMD(this.props.mediaSize) && !!this.props.searchValue,
            });
            this.props.setSearchOutsideMailbox(this.props.isMailbox);
        }
    }

    handleMobileOutsideClick = (event: MouseEvent | TouchEvent): void => {
        const node = event.target as Element;

        if (
            this.state.showMobileSearch &&
            ((this.buttonRef.current && this.buttonRef.current.contains(node)) ||
                (this.mailSearch.current && this.mailSearch.current.contains(node)))
        ) {
            return;
        }

        this.setState({ showMobileSearch: false });

        if (this.sidebarButton.current && this.sidebarButton.current.contains(node)) {
            return;
        }

        if (this.props.sidebarShown) {
            this.showHideSideBar();
        }
    };

    showHideSideBar = (): void => {
        this.props.sidebarShown ? this.props.hideSidebar() : this.props.showSidebar();
    };

    sidebarClickAction = (event: React.MouseEvent): void => {
        event && event.preventDefault();
        this.showHideSideBar();
    };

    toggleSearchField = (event: React.MouseEvent): void => {
        event && event.preventDefault();
        this.setState((state) => ({
            showMobileSearch: !state.showMobileSearch,
        }));
    };

    onLogoClick = (): void => {
        if (this.props.sidebarShown) {
            this.props.hideSidebar();
        }

        this.props.selectFolder(null);
    };

    toggleSidebar = () => {
        this.props.sidebarShown ? this.props.hideSidebar() : this.props.showSidebar();
    };

    public render(): JSX.Element {
        const { logos, links, colors } = this.context;
        const { isEmpty, isMailbox = false, logoutUser, mediaSize, withLogo, withSearch = true } = this.props;
        const { showMobileSearch } = this.state;

        const { useNgdp } = staticConfig;

        const shouldDisplayHeaderTabs = useNgdp === false;

        const headerLogoId = String(logos.header.logo);
        const headerLogoMediumId = String(logos.header.logoMedium);
        const headerLogoSmallId = String(logos.header.logoSmall);
        const headerHelpLink = links.header.help.link;
        const headerHelpLabel = links.header.help.label;
        const logoBackgroundColor = colors.header.logoBackground;
        const headerBackGroundColor = shouldDisplayHeaderTabs ? colors.header.logoBackground : colors.header.background;
        const primaryButtonBackgroundColor = colors.buttons.primary.background;
        const headerLogo = isMediaSizeLessThanLG(mediaSize) ? headerLogoSmallId : headerLogoId;

        const classicLogonPlace = localStorage.getItem('classicLogonPlace');
        const classicMailboxLink = staticConfig.classicMailboxLink.replace('{domainType}', classicLogonPlace);
        const classicOverviewLink = staticConfig.classicOverviewLink.replace('{domainType}', classicLogonPlace);

        const LogoLink: React.FC = ({ children }) => {
            if (!staticConfig.isclassicunavailable) {
                return useNgdp ? <Link to="/mailbox">{children}</Link> : <a href={classicOverviewLink}>{children}</a>;
            } else {
                return <a href="/mailbox">{children}</a>;
            }
        };

        const role = '';

        return (
            <>
                <header className={`${styles.header}`} style={{ backgroundColor: headerBackGroundColor }}>
                    {isEmpty ? (
                        withLogo && (
                            <div
                                className={`${styles.onboardingLogo} d-flex align-items-center justify-content-center align-self-stretch`}
                                style={{ backgroundColor: logoBackgroundColor }}
                            >
                                <LogoLink>
                                    <StaticResource imageId={headerLogoId} className={styles.logo} alt="LOGO" />
                                </LogoLink>
                            </div>
                        )
                    ) : !isMediaSizeLessThanMD(mediaSize) ? (
                        <>
                            <div className={`${styles.logo}`}>
                                <LogoLink>
                                    <StaticResource imageId={headerLogo} alt="LOGO" />
                                </LogoLink>
                            </div>

                            <div className={`${styles.nav}`}>
                                <MailSearch isMailbox={isMailbox} show={withSearch} />

                                <HeaderMenu />

                                <Button className={`mr-2 ${styles.headerButton} prevFocusedElem`} link variant="black">
                                    <CustomLink
                                        tabIndex={-1}
                                        className="styleless"
                                        href={headerHelpLink}
                                        target="_blank"
                                        variant="light"
                                    >
                                        <FormattedMessage id={headerHelpLabel} />
                                    </CustomLink>
                                </Button>

                                <Button
                                    id="logoff"
                                    className={`prevFocusedElem ${styles.headerButton}`}
                                    variant="black"
                                    onClick={logoutUser}
                                >
                                    <FormattedMessage id="LOG_OFF" />
                                </Button>
                            </div>
                        </>
                    ) : (
                        <div className="d-flex p-0 m-0 w-100 justify-content-space">
                            {/* {!withSearch ? null : (
                                <button
                                    ref={this.sidebarButton}
                                    className={`d-flex align-items-center justify-content-center ${styles.sidebarButton}`}
                                    onClick={this.sidebarClickAction}
                                >
                                    <Image src={images.folder} alt="MENU" />
                                </button>
                            )} */}

                            <div
                                className={`d-md-none d-lg-none d-xl-none d-xxl-noned-flex flex align-items-center ${styles.folderIconMob}`}
                            >
                                <Image
                                    tabIndex={0}
                                    role={role}
                                    onClick={this.toggleSidebar}
                                    src={images.folder}
                                    alt="FOLDERS"
                                />
                            </div>

                            <div className="d-flex flex-fill align-items-center justify-content-center">
                                <LogoLink>
                                    <StaticResource
                                        imageId={headerLogoMediumId || headerLogoId}
                                        alt="LOGO"
                                        className={styles.logo}
                                    />
                                </LogoLink>
                            </div>

                            {!withSearch ? null : (
                                <div className="d-flex align-items-center justify-content-end">
                                    <div
                                        className={`${styles.searchButton} d-flex align-items-center justify-content-center h-100`}
                                        style={{ backgroundColor: primaryButtonBackgroundColor }}
                                    >
                                        <a
                                            ref={this.buttonRef}
                                            className="d-flex align-items-center justify-content-center w-100 h-100"
                                            href={staticConfig.defaultUrl}
                                            onClick={this.toggleSearchField}
                                        >
                                            <Image src={images.searchLink} alt="SEARCH" />
                                        </a>
                                    </div>
                                </div>
                            )}

                            <HeaderMenu />
                        </div>
                    )}
                </header>

                <div ref={this.mailSearch}>
                    <MailSearch
                        isMailbox={this.props.isMailbox}
                        show={showMobileSearch && withSearch && isMediaSizeLessThanMD(this.props.mediaSize)}
                    />
                </div>

                <HeaderTabs
                    display={shouldDisplayHeaderTabs}
                    mailboxLink={classicMailboxLink}
                    overviewLink={classicOverviewLink}
                />
            </>
        );
    }
}

const mapDispatcherToProps = (dispatch: Dispatch) => {
    return {
        logoutUser: () => {
            getUserInfo().then((res) => {
                dispatch(logoutUser());
            });
        },
        showSidebar: () => dispatch(showSidebar()),
        hideSidebar: () => dispatch(hideSidebar()),
        selectFolder: (folder: ISelectedFolder) => dispatch(selectFolder(folder)),
        setSearchOutsideMailbox: (enabled: boolean) => dispatch(setSearchOutsideMailbox(enabled)),
    };
};

const mapStateToProps = ({ appReadouts, sidebar, mailbox }: IRootState) => {
    return {
        mediaSize: appReadouts.mediaSize,
        sidebarShown: sidebar.sidebarShown,
        searchValue: mailbox.searchValue,
        selectedFolder: mailbox.selectedFolder,
    };
};

type ReduxType = IUserHeaderProps &
    ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatcherToProps> &
    RouteComponentProps;

export default withRouter(connect(mapStateToProps, mapDispatcherToProps)(UserHeader));
